import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../../ui/dialog";
import React, { useState } from "react";
import Button from "../../form/button";
import ErrorMessage from "../../form/errorMessage";
import { IFolder, IFolderRecord, Status } from "../../../lib/types";
import { Input } from "../../ui/input";
import pb from "../../../lib/pocketbase";
import { config } from "../../../config";
import { Link } from "react-router-dom";
import { createSlug } from "../../../lib/utils";
import { Switch } from "../../ui/switch";

interface NewFolderProps {
    folder: IFolderRecord | undefined;
    reload: () => void;
}

export default function EditFolder({ folder, reload }: NewFolderProps) {
    const [name, setName] = useState(folder?.name);
    const [isPublic, setIsPublic] = useState(folder?.public);
    const [open, setOpen] = useState(false);
    const [error, setError] = useState("");
    const [status, setStatus] = useState<Status>("none");

    function update() {
        if (!folder) return;
        setStatus("loading");

        pb.collection("folder")
            .update(folder.id, {
                name: name,
                public: isPublic
            })
            .then(() => {
                setTimeout(() => {
                    setStatus("success");
                    setError("");
                    reload();
                    setOpen(false);
                }, 500);
            })
            .catch((err: Error) => {
                setStatus("error");
                setError(err.message);
            })
            .finally(() => {
                setTimeout(() => {
                    setStatus("none");
                }, 3000);
            });
    }

    const slug = createSlug(name || "");

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <div className="flex cursor-pointer items-center gap-3 px-4 py-2 hover:opacity-80">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
                    </svg>
                    Edit
                </div>
            </DialogTrigger>
            <DialogContent className="mt-8 md:mt-0">
                <DialogHeader>
                    <DialogTitle>Edit folder</DialogTitle>
                    <DialogDescription>Update this folder</DialogDescription>
                </DialogHeader>
                <ErrorMessage error={error} close={() => setError("")} />
                <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && update()} placeholder="Folder name" />
                <p className="flex items-center gap-2">
                    Public: <Switch checked={isPublic} onCheckedChange={setIsPublic} />
                </p>
                {isPublic && (
                    <div className="flex items-center gap-2 rounded bg-slate-100 px-4 py-2 text-slate-500 dark:bg-slate-800 dark:text-slate-500">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
                        </svg>
                        <Link target="_blank" to={`${config.base}/folder/${slug}/${folder?.id}`}>{`${config.base}/folder/${slug}/${folder?.id}`}</Link>
                    </div>
                )}
                <DialogFooter className="gap-2">
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button status={status} onClick={() => update()} variant="dark">
                        {status === "loading" && "Changing"}
                        {status === "success" && "Changed"}
                        {status === "error" && "Error"}
                        {status === "none" && "Change"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
